'use client';

import { SessionProvider } from 'next-auth/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { useState, useEffect } from 'react';
import { useSession } from 'next-auth/react';
import { useNotificationStore } from '@/store/notificationStore';
import { I18nProvider } from '@/lib/i18n/context';
import { LanguagePrompt } from './LanguagePrompt';

function NotificationPoller() {
  const { data: session } = useSession();
  const { setUnreadCount } = useNotificationStore();

  useEffect(() => {
    if (!session) return;
    const fetchCount = async () => {
      try {
        const res = await fetch('/api/notifications');
        if (!res.ok) return;
        const data = await res.json();
        setUnreadCount(data.unreadCount ?? 0);
      } catch { /* silent */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, 60_000);
    return () => clearInterval(id);
  }, [session, setUnreadCount]);

  return null;
}

export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () => new QueryClient({
      defaultOptions: {
        queries: { staleTime: 60 * 1000, refetchOnWindowFocus: false },
      },
    })
  );

  return (
    <SessionProvider>
      <QueryClientProvider client={queryClient}>
        {/* I18nProvider wraps everything so all components can access translations */}
        <I18nProvider>
          <NotificationPoller />
          <LanguagePrompt />
          {children}
          <Toaster
            position="bottom-right"
            toastOptions={{
              style: {
                background: '#1a1a1a',
                color: '#e5e5e5',
                border: '1px solid #333',
                borderRadius: '16px',
                fontFamily: 'var(--font-body)',
                fontSize: '14px',
              },
              success: {
                iconTheme: { primary: '#f72b72', secondary: '#fff' },
              },
              error: {
                iconTheme: { primary: '#ef4444', secondary: '#fff' },
              },
            }}
          />
        </I18nProvider>
      </QueryClientProvider>
    </SessionProvider>
  );
}
